import React from 'react';
import { IconName, Icon, IconColor } from 'app/common/components/controls';
import { Tooltip, TooltipPrimitiveLight } from '@trello/nachos';
import classNames from 'classnames';
import styles from './PlanSelectionOld.less';
import RouterLink from 'app/common/components/singlespa/router-link';
import { forNamespace } from 'app/common/lib/util/i18n';
import { OrganizationFeatures } from '@trello/product-features';
import { formatMoneyLocalized } from 'app/scripts/lib/format/money';

const format = forNamespace('upgrade prompt plan selection');
const getPlanFeatureStrings = (plan: string, feature: string) => {
  const namespace = [plan, 'features', feature];

  return {
    title: plan === 'free team' ? null : format([...namespace, 'title']),
    content: format([...namespace, 'content']),
    tooltip:
      plan === 'free team' || feature === 'boards'
        ? null
        : format([...namespace, 'tooltip']),
  };
};

// exported for testing
export const bcPlanSelectionList = [
  {
    icon: IconName.Board,
    ...getPlanFeatureStrings('bc team', 'boards'),
  },
  {
    icon: IconName.PowerUp,
    ...getPlanFeatureStrings('bc team', 'pups'),
  },
  {
    icon: IconName.ButlerBot,
    ...getPlanFeatureStrings('bc team', 'butler'),
  },
  {
    icon: IconName.Subscribe,
    ...getPlanFeatureStrings('bc team', 'admin'),
  },
  {
    icon: IconName.Heart,
    ...getPlanFeatureStrings('bc team', 'support'),
  },
  {
    icon: IconName.Star,
    ...getPlanFeatureStrings('bc team', 'more'),
  },
];

const freeTeamSelectionList = [
  {
    icon: IconName.Board,
    ...getPlanFeatureStrings('free team', 'boards'),
  },
  {
    icon: IconName.PowerUp,
    ...getPlanFeatureStrings('free team', 'pups'),
  },
  {
    icon: IconName.ButlerBot,
    ...getPlanFeatureStrings('free team', 'butler'),
  },
];

const renderTooltip = (tooltip: string | null, target: React.ReactNode) =>
  tooltip ? (
    <Tooltip content={tooltip} component={TooltipPrimitiveLight}>
      {target}
    </Tooltip>
  ) : (
    target
  );

interface PlanSelectionOldProps {
  onContinue(): void;
  onSkip(): void;
  ctaLink?: string;
  showPlanButtons?: boolean;
  orgId?: string;
}

const pricePerUser = format('price per user', {
  price: formatMoneyLocalized(
    OrganizationFeatures.getPrice(
      OrganizationFeatures.currentProducts.yearly,
    )! / 12,
  ),
});

export const PlanSelectionOld: React.FunctionComponent<PlanSelectionOldProps> = ({
  onContinue,
  onSkip,
  ctaLink,
  showPlanButtons,
}) => {
  return (
    <>
      <div className={styles.heading}>
        <h1>
          <Icon
            name={IconName.BusinessClass}
            color={IconColor.Dark}
            className={styles.headingIcon}
          />{' '}
          {format('headline')}
        </h1>
        <h2>{format('description')}</h2>
      </div>

      <div className={styles.bodyWrapper}>
        <div className={styles.freePlan}>
          <h2>{format(['free team', 'headline'])}</h2>
          <ul>
            {freeTeamSelectionList.map(({ icon, content }, index) => (
              <li key={index} className={styles.featureItem}>
                <Icon name={icon} medium className={styles.featureIcon} />
                <span className={styles.featureContent}>{content}</span>
              </li>
            ))}
          </ul>
          {showPlanButtons && (
            <button className={styles.getStarted} onClick={onSkip}>
              {format('get started')}
            </button>
          )}
        </div>

        <div className={styles.bcPlan}>
          <div className={styles.recommendedLabel}>Recommended</div>
          <h2>{format(['bc team', 'headline'])}</h2>
          <ul>
            {bcPlanSelectionList.map(
              ({ icon, title, content, tooltip }, index) => (
                <li
                  key={index}
                  className={classNames(
                    styles.featureItem,
                    !!tooltip && styles.tooltipTarget,
                  )}
                >
                  {renderTooltip(
                    tooltip,
                    <div className={styles.featureItemWrapper}>
                      <Icon name={icon} medium className={styles.featureIcon} />
                      <div>
                        <span className={styles.featureTitle}>
                          {title} {''}
                          <span className={styles.featureContent}>
                            {content}
                          </span>
                        </span>
                      </div>
                    </div>,
                  )}
                </li>
              ),
            )}
          </ul>
          {showPlanButtons && (
            <>
              <p>{pricePerUser}</p>
              <button
                data-test-id="planSelectionButton"
                className={styles.ctaButton}
                onClick={onContinue}
              >
                {format('start bc')}
              </button>
            </>
          )}
        </div>
      </div>
      {!showPlanButtons && (
        <div className={styles.actions}>
          <p>{pricePerUser}</p>
          {!!ctaLink && (
            <RouterLink
              href={ctaLink}
              className={styles.ctaButton}
              onClick={onContinue}
            >
              {format('upgrade bc')}
            </RouterLink>
          )}
          <a href="/business-class" className={styles.learnMore}>
            {format('learn bc')}
          </a>
        </div>
      )}
    </>
  );
};
